import { encodeFunctionData } from "viem"
import { useAccount, useEstimateGas, useGasPrice } from "wagmi"

import { selectIsConnected, useDappSelector } from "#/redux-state"
import {
  bridge,
  ETHEREUM_CHAIN_ID,
  ONE_SECOND_IN_MILLISECONDS,
} from "#/shared/constants"
import Portal from "#/shared/constants/artifacts/Portal"
import { BridgeableTokens } from "#/shared/types"

const refetchInterval = ONE_SECOND_IN_MILLISECONDS * 10 // 10 seconds
const staleTime = refetchInterval

export default function useEstimateBridgeDepositGas(
  token: BridgeableTokens,
  depositId?: number,
  isEnabled = true,
) {
  const { address } = useAccount()
  const isConnected = useDappSelector(selectIsConnected)
  const { sourceToken } = bridge.BRIDGEABLE_TOKENS[token]
  const isQueryEnabled = !!address && isConnected && !!depositId && isEnabled

  const { data: gasAmount, isFetching: isFetchingGasAmount } = useEstimateGas({
    to: Portal.address,
    data: encodeFunctionData({
      abi: Portal.abi,
      functionName: "bridgeDeposit",
      args: [sourceToken, BigInt(depositId ?? 0n)],
    }),
    chainId: ETHEREUM_CHAIN_ID,
    query: {
      enabled: isQueryEnabled,
      staleTime,
      refetchInterval,
    },
  })

  const { data: gasPrice, isFetching: isFetchingGasPrice } = useGasPrice({
    chainId: ETHEREUM_CHAIN_ID,
    query: {
      enabled: !!gasAmount && isQueryEnabled,
      staleTime,
      refetchInterval,
    },
  })

  return {
    isFetching: isFetchingGasAmount || isFetchingGasPrice,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee: (gasAmount ?? 0n) * (gasPrice ?? 0n),
  }
}
